/**
 * Chat Input Component
 * Handles text input, file uploads and image uploads
 */

import React, { useState } from 'react';
import { Send, X, Paperclip, Loader2, XCircle, FileText, FileSpreadsheet, File, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPES } from './chatbotConfig';

const ChatInput = ({
  language,
  inputValue,
  setInputValue,
  isLoading,
  isUploading,
  uploadedFiles,
  uploadedImages,
  onSendMessage,
  onClose,
  onFileUpload,
  onImageUpload,
  onRemoveFile,
  onRemoveImage,
  fileInputRef,
  imageInputRef,
  inputRef,
  messages,
  t
}) => {
  // Pilot version warning state
  const [showPilotWarning, setShowPilotWarning] = useState(false);
  const [pilotWarningType, setPilotWarningType] = useState(''); // 'file' or 'image'
  
  // Handle pilot feature click
  const handlePilotFeatureClick = (type) => {
    setPilotWarningType(type);
    setShowPilotWarning(true);
    // Auto-hide after 4 seconds
    setTimeout(() => setShowPilotWarning(false), 4000);
  };
  
  // Get file icon based on extension
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="w-4 h-4 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
    if (['ppt', 'pptx'].includes(ext)) return <File className="w-4 h-4 text-orange-500" />;
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return <ImageIcon className="w-4 h-4 text-purple-500" />;
    return <File className="w-4 h-4 text-slate-500" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isLoading) {
        onSendMessage(inputValue);
      }
    }
  };

  return (
    <div className="p-4 border-t border-slate-200">
      {/* Uploaded Files and Images Display */}
      {(uploadedFiles.length > 0 || uploadedImages.length > 0) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {uploadedFiles.map(file => (
            <div 
              key={file.id}
              className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 text-sm group"
            >
              {getFileIcon(file.filename)}
              <span className="text-slate-700 max-w-[120px] truncate">{file.filename}</span>
              <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
              <button
                onClick={() => onRemoveFile(file.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                title={language === 'pt' ? 'Remover' : 'Remove'}
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          ))}
          {uploadedImages.map(img => (
            <div 
              key={img.id}
              className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-1.5 text-sm group border border-purple-100"
            >
              <ImageIcon className="w-4 h-4 text-purple-500" />
              <span className="text-slate-700 max-w-[120px] truncate">{img.filename}</span>
              <span className="text-xs text-slate-400">{formatFileSize(img.size)}</span>
              <button
                onClick={() => onRemoveImage(img.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                title={language === 'pt' ? 'Remover' : 'Remove'}
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES}
        onChange={onFileUpload}
        className="hidden"
        data-testid="file-upload-input"
      />
      
      {/* Hidden image input */}
      <input
        ref={imageInputRef}
        type="file"
        accept={ALLOWED_IMAGE_TYPES}
        onChange={onImageUpload}
        className="hidden"
        data-testid="image-upload-input"
      />
      
      <div className="flex gap-2 relative">
        <button
          onClick={onClose}
          className="px-3 py-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
          title={language === 'pt' ? 'Fechar chat' : 'Close chat'}
          data-testid="chatbot-close-btn-bottom"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* File upload button - DISABLED in pilot version */}
        <button
          onClick={() => handlePilotFeatureClick('file')}
          className="px-3 py-2 border border-slate-200 rounded-lg transition-colors text-slate-400 bg-slate-50 cursor-not-allowed opacity-60"
          title={language === 'pt' ? 'Funcionalidade indisponível na versão piloto' : 'Feature unavailable in pilot version'}
          data-testid="file-upload-btn"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        
        {/* Image upload button - DISABLED in pilot version */}
        <button
          onClick={() => handlePilotFeatureClick('image')}
          className="px-3 py-2 border border-slate-200 rounded-lg transition-colors text-slate-400 bg-slate-50 cursor-not-allowed opacity-60"
          title={language === 'pt' ? 'Funcionalidade indisponível na versão piloto' : 'Feature unavailable in pilot version'}
          data-testid="image-upload-btn"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        
        {/* Pilot version warning toast */}
        {showPilotWarning && (
          <div className="absolute bottom-full left-0 mb-2 w-full px-2 z-50 animate-slideUp">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 shadow-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  {language === 'pt' ? 'Versão Piloto' : 'Pilot Version'}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {language === 'pt' 
                    ? `O carregamento de ${pilotWarningType === 'file' ? 'documentos' : 'imagens'} não está disponível nesta versão piloto.`
                    : `${pilotWarningType === 'file' ? 'Document' : 'Image'} upload is not available in this pilot version.`
                  }
                </p>
              </div>
              <button 
                onClick={() => setShowPilotWarning(false)}
                className="text-amber-600 hover:text-amber-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            uploadedImages.length > 0 
              ? (language === 'pt' ? 'Pergunte sobre a imagem...' : 'Ask about the image...')
              : uploadedFiles.length > 0 
                ? (language === 'pt' ? 'Pergunte sobre o documento...' : 'Ask about the document...')
                : t('askQuestion')
          }
          className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          data-testid="chatbot-input"
          disabled={isLoading}
        />
        <Button
          onClick={() => onSendMessage(inputValue)}
          disabled={!inputValue.trim() || isLoading}
          className="bg-primary hover:bg-primary/90"
          data-testid="chatbot-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Upload hint */}
      {uploadedFiles.length === 0 && uploadedImages.length === 0 && !isLoading && messages.length === 0 && (
        <p className="text-xs text-slate-400 mt-2 text-center">
          {language === 'pt' 
            ? '💡 Pode anexar documentos (PDF, Word, Excel, PPT) ou imagens para análise' 
            : '💡 You can attach documents (PDF, Word, Excel, PPT) or images for analysis'}
        </p>
      )}
    </div>
  );
};

export default ChatInput;
